/* eslint-disable no-warning-comments */
import {Tab} from '@headlessui/react'
import classNames from 'classnames'
import {gsap} from 'gsap'
import React, {createRef, Fragment} from 'react'

import {makeEditable} from '@/common/utils'
import Buttons from '@/components/Buttons'
import Content from '@/components/Content'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import Image from '@/components/Image'

const Tabs = ({backgroundColors, headingTag, heading, items, _editable}) => {
  const HeadingTag = headingTag || 'p'
  const tabListGridRef = createRef()
  const progressRef = React.useRef([])
  const tabsRef = React.useRef([])

  const [selectedIndex, setSelectedIndex] = React.useState(0)

  const isanimating = React.useRef(null)
  const interval = React.useRef(null)
  const intervaltime = React.useRef(10000)

  const switchSlide = (i) => {
    setSelectedIndex(i)
  }
  const handleClick = (i) => {
    if (!isanimating.current) {
      intervaltime.current = 10000
      interval.current = clearInterval(interval.current)
      interval.current = null
      switchSlide(i)
    }
  }

  React.useEffect(() => {
    const context = gsap.context(() => {
      const tl = gsap.timeline()
      if (interval.current === null) {
        tl.add(function () {
          interval.current = setInterval(() => {
            setSelectedIndex((prevNewIndex) =>
              prevNewIndex + 1 < items.length ? prevNewIndex + 1 : 0
            )
          }, intervaltime.current)
        })
      }
      tl.to(progressRef.current[selectedIndex], {
        width: '100%',
        duration: window.innerWidth > 680 ? 14 : 10,
        ease: 'none',
      })
    })

    return () => context.revert()
  }, [selectedIndex, items.length])

  return (
    <section {...makeEditable(_editable)} className={classNames('py-30', backgroundColors.current)}>
      <Grid className='relative'>
        <div className='col-span-3 mb-38 md:col-span-4 md:mb-16 lg:col-span-5'>
          <HeadingTag>
            <Heading heading={heading} />
          </HeadingTag>
        </div>

        <DoodleArrow
          aria-hidden
          className='absolute right-1/4 top-26 md:left-[60%] md:top-10 lg:left-[40%] lg:top-1/4'
        />
      </Grid>

      {items.length > 0 && (
        <Tab.Group as={Fragment} selectedIndex={selectedIndex}>
          <div className='relative md:pl-2'>
            <Grid ref={tabListGridRef}>
              <Tab.List as={Fragment}>
                <div className='col-span-full row-start-2 md:col-start-7 md:row-start-1 lg:col-start-11'>
                  <div className='mt-10 flex w-full flex-row gap-2 overflow-auto md:mt-0 md:flex-col'>
                    {items.map(({_uid, logo}, index) => {
                      return (
                        <Tab
                          key={_uid}
                          className={classNames(
                            'group relative flex-1 overflow-hidden whitespace-nowrap transition duration-200',
                            'relative w-full',
                            'after:absolute after:top-0 after:left-0 after:block after:h-[2px] after:w-full after:bg-purple-300 after:bg-opacity-100 after:content-[attr(after)] md:after:bottom-0 md:after:h-full md:after:w-[2px]',
                            'py-4 px-2 md:px-4 lg:py-0',
                            'focus:outline-none focus:ring-0'
                          )}
                          onClick={() => {
                            handleClick(index)
                          }}
                        >
                          <div
                            className='absolute top-0 left-0 z-10 h-[2px] w-0 bg-purple-900 md:left-[1px] md:-top-[1px] md:origin-left md:rotate-90'
                            ref={(ref) => (progressRef.current[index] = ref)}
                          ></div>
                          <Image
                            className={classNames(
                              'w-36 transition-all duration-150 md:block md:h-34 lg:h-30',
                              {
                                'opacity-40': selectedIndex !== index,
                              }
                            )}
                            {...logo}
                            sizes={{sm: 33, md: 20}}
                            ratio='1:1'
                            fitIn
                          />
                        </Tab>
                      )
                    })}
                  </div>
                </div>
              </Tab.List>

              <Tab.Panels as={Fragment}>
                {items.map(({_uid, heading, content, icon, image, buttons}, index) => {
                  return (
                    <Tab.Panel
                      as={Fragment}
                      key={_uid}
                      ref={(ref) => (tabsRef.current[index] = ref)}
                      className='tabsActive'
                    >
                      <div className='col-span-full row-start-1 md:col-span-6 md:mb-0 md:pr-4 lg:col-span-10'>
                        <div className='group relative mb-10 flex h-full flex-col justify-end gap-4 overflow-hidden p-10'>
                          {image && image.filename && (
                            <div className='absolute inset-0 z-0 overflow-hidden rounded-2xl'>
                              <Image
                                className='h-full w-full animate-scaleDown rounded-2xl object-cover transition duration-200 group-hover:scale-110'
                                {...image}
                                sizes={{md: 50, lg: 66}}
                                ratio='16:9'
                              />
                            </div>
                          )}
                          <div className={classNames('mt-51 flex items-center gap-4 md:mt-0')}>
                            {icon && (
                              <div className='relative z-20 h-12 w-12 animate-fade rounded-xl bg-purple-900 p-3'>
                                <Image {...icon} ratio='1:1' fitIn sizes={{sm: '24', unit: 'px'}} />
                              </div>
                            )}
                            <span className='heading-xs-millik relative z-20 animate-fade text-left text-purple-900'>
                              {heading}
                            </span>
                          </div>

                          <Content
                            content={content}
                            className='relative z-20 mx-auto w-full max-w-[536px] animate-fade line-clamp-5 md:mx-0 md:text-left lg:line-clamp-3'
                          />
                          <Buttons
                            buttons={buttons}
                            className='button-secondary relative	z-20 flex animate-fade flex-col gap-y-3'
                          />
                          <div className='absolute bottom-0 left-0 h-full w-full rounded-2xl bg-[radial-gradient(ellipse_at_bottom_left,#9BEDED,#9BEDED,transparent,transparent)] opacity-80' />
                        </div>
                      </div>
                    </Tab.Panel>
                  )
                })}
              </Tab.Panels>
            </Grid>
          </div>
        </Tab.Group>
      )}
    </section>
  )
}

const DoodleArrow = (props) => (
  <svg
    width='137'
    height='132'
    viewBox='0 0 137 132'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M21.3605 3.94267C20.9359 3.58946 20.878 2.95894 21.2312 2.53438C21.5845 2.1098 22.215 2.05196 22.6395 2.40517L21.3605 3.94267ZM24.6975 89.0417L25.072 88.1145L24.6975 89.0417ZM127.676 126.968C127.245 127.313 126.616 127.244 126.27 126.813L120.644 119.789C120.299 119.358 120.368 118.728 120.799 118.383C121.23 118.038 121.859 118.107 122.205 118.538L127.206 124.782L133.45 119.781C133.881 119.435 134.51 119.505 134.856 119.936C135.201 120.367 135.131 120.996 134.7 121.342L127.676 126.968ZM22.6395 2.40517C32.8596 10.9075 42.3087 22.248 49.695 34.0466C57.077 45.8382 62.4386 58.1514 64.4266 68.6163C66.3995 79.0022 65.1083 87.983 58.5614 92.504C55.3099 94.7494 50.8932 95.7855 45.2512 95.4349C39.612 95.0844 32.6843 93.3462 24.3229 89.969L25.072 88.1145C33.3164 91.4445 40.0242 93.1062 45.3753 93.4387C50.7236 93.7711 54.6519 92.7733 57.4249 90.8583C62.9273 87.0586 64.4037 79.2128 62.4617 68.9896C60.5347 58.8454 55.2988 46.767 47.9998 35.1078C40.7051 23.4556 31.3895 12.2861 21.3605 3.94267L22.6395 2.40517ZM24.3229 89.969C13.6769 85.6689 7.34171 80.6144 4.61947 75.216C1.85199 69.7278 2.92203 64.1075 6.53851 59.0394C10.1299 54.0064 16.2642 49.4468 23.8441 45.8398C31.4389 42.2257 40.5576 39.5329 50.203 38.2792C69.4711 35.7747 91.0153 38.9966 106.729 52.3309C122.487 65.7017 132.16 89.0471 128.045 126.298L126.057 126.078C130.124 89.2638 120.535 66.6686 105.435 53.8559C90.2929 41.0067 69.3864 37.8025 50.4608 40.2625C41.0094 41.491 32.0955 44.1281 24.7034 47.6458C17.2964 51.1705 11.4894 55.5445 8.16652 60.2011C4.86874 64.8226 4.05156 69.6478 6.40527 74.3155C8.80421 79.0728 14.5986 83.8842 25.072 88.1145L24.3229 89.969Z'
      fill='#7F6AFF'
    />
  </svg>
)

export default Tabs
